import { Host, h } from "@stencil/core";
import { DISABLED_CLASS } from "../../common/reserverd-names";
const CHECKBOX_ID = "checkbox";
const PARTS = (checked, indeterminate) => `${checked ? " checked" : ""}${indeterminate ? " indeterminate" : ""}`;
export class CheckBox {
    constructor() {
        this.accessibleName = undefined;
        this.caption = undefined;
        this.checked = undefined;
        this.checkedValue = undefined;
        this.disabled = false;
        this.highlightable = false;
        this.indeterminate = false;
        this.readonly = false;
        this.unCheckedValue = undefined;
        this.value = undefined;
    }
    element;
    /**
     * Emitted when the element is clicked or the space key is pressed and
     * released.
     */
    click;
    /**
     * The `input` event is emitted when a change to the element's value is
     * committed by the user.
     */
    input;
    valueChanged() {
        this.checked = this.value === this.checkedValue;
    }
    componentWillLoad() {
        this.checked = this.value === this.checkedValue;
    }
    getValue = (checked) => checked ? this.checkedValue : this.unCheckedValue;
    /**
     * Checks if it is necessary to prevent the click from bubbling
     */
    handleClick = (event) => {
        if (this.readonly || this.disabled) {
            return;
        }
        event.stopPropagation();
    };
    handleChange = (event) => {
        event.stopPropagation();
        const inputRef = event.target;
        const checked = inputRef.checked;
        const value = this.getValue(checked);
        this.checked = checked;
        this.value = value;
        inputRef.value = value; // Update input's value before emitting the event
        this.input.emit(event);
        if (this.highlightable) {
            this.click.emit();
        }
    };
    render() {
        const additionalParts = PARTS(this.checked, this.indeterminate);
        return (h(Host, { class: {
                [DISABLED_CLASS]: this.disabled,
                "ch-checkbox--actionable": (!this.readonly && !this.disabled) ||
                    (this.readonly && this.highlightable)
            } }, h("div", { class: {
                container: true,
                "container--checked": this.checked
            }, part: `container${additionalParts}` }, h("input", { "aria-label": this.accessibleName?.trim() !== "" &&
                this.accessibleName !== this.caption
                ? this.accessibleName
                : null, id: this.caption ? CHECKBOX_ID : null, class: "input", part: "input", type: "checkbox", checked: this.checked, disabled: this.disabled || this.readonly, indeterminate: this.indeterminate, value: this.value, onClick: this.handleClick, onInput: this.handleChange }), h("div", { class: {
                option: true,
                "option--checked": this.checked && !this.indeterminate,
                "option--indeterminate": this.indeterminate
            }, part: `option${additionalParts}`, "aria-hidden": "true" })), this.caption && (h("label", { class: "label", part: "label", htmlFor: CHECKBOX_ID, onClick: this.handleClick }, this.caption))));
    }
    static get is() { return "ch-checkbox"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["checkbox.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["checkbox.css"]
        };
    }
    static get properties() {
        return {
            "accessibleName": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies a short string, typically 1 to 3 words, that authors associate\nwith an element to provide users of assistive technologies with a label\nfor the element."
                },
                "attribute": "accessible-name",
                "reflect": false
            },
            "caption": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the label of the checkbox."
                },
                "attribute": "caption",
                "reflect": false
            },
            "checked": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Indicates that the control is selected by default."
                },
                "attribute": "checked",
                "reflect": false
            },
            "checkedValue": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The value when the checkbox is 'on'"
                },
                "attribute": "checked-value",
                "reflect": false
            },
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if the element is disabled.\nIf disabled, it will not fire any user interaction related event\n(for example, click event)."
                },
                "attribute": "disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "highlightable": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "True to highlight control when an action is fired."
                },
                "attribute": "highlightable",
                "reflect": false,
                "defaultValue": "false"
            },
            "indeterminate": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` if the control's value is indeterminate."
                },
                "attribute": "indeterminate",
                "reflect": false,
                "defaultValue": "false"
            },
            "readonly": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute indicates that the user cannot modify the value of the control.\nSame as [readonly](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#attr-readonly)\nattribute for `input` elements."
                },
                "attribute": "readonly",
                "reflect": false,
                "defaultValue": "false"
            },
            "unCheckedValue": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The value when the checkbox is 'off'"
                },
                "attribute": "un-checked-value",
                "reflect": false
            },
            "value": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The value of the control."
                },
                "attribute": "value",
                "reflect": false
            }
        };
    }
    static get events() {
        return [{
                "method": "click",
                "name": "click",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted when the element is clicked or the space key is pressed and\nreleased."
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "input",
                "name": "input",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "The `input` event is emitted when a change to the element's value is\ncommitted by the user."
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }];
    }
    static get elementRef() { return "element"; }
    static get watchers() {
        return [{
                "propName": "value",
                "methodName": "valueChanged"
            }];
    }
}
//# sourceMappingURL=checkbox.js.map
